import { setupI18n } from "@lingui/core";
import { messages as enMessages } from "../locales/en/messages";
import { messages as idMessages } from "../locales/id/messages";
import { messages as jaMessages } from "../locales/ja/messages";
import { messages as koMessages } from "../locales/ko/messages";
import { messages as neMessages } from "../locales/ne/messages";
import { messages as viMessages } from "../locales/vi/messages";
import { messages as zhHansMessages } from "../locales/zh-Hans/messages";
import { messages as zhHantMessages } from "../locales/zh-Hant/messages";

export const SUPPORTED_LOCALES = [
  "ja",
  "en",
  "zh-Hans",
  "zh-Hant",
  "ko",
  "vi",
  "id",
  "ne",
] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

const FALLBACK_LOCALE: SupportedLocale = "ja";

/**
 * BCP 47 タグを「言語＋文字体系」に畳む。言語サブタグだけでは足りないのは、同じ言語でも
 * 文字体系で文言が別物になる場合があるため（zh-TW と zh-CN など）。ブラウザは文字体系を
 * 省いた zh-TW の形で渡してくるので、maximize() で補ってから突き合わせる。
 * maximize() を持たない古い WebView では言語サブタグだけの比較に落ちる。
 */
function toLocaleKey(tag: string): string {
  try {
    const { language, script } = new Intl.Locale(tag).maximize();
    return script ? `${language}-${script}` : language;
  } catch {
    return tag.split("-")[0].toLowerCase();
  }
}

// ロケールはページ読み込み時にブラウザの言語設定から決めて、以降は固定する。読み込み後の
// 切り替えは行わないため I18nProvider は使わず、t() は React を介さずこのシングルトンを直接引く
function resolveLocale(): SupportedLocale {
  const byKey = new Map(
    SUPPORTED_LOCALES.map((locale) => [toLocaleKey(locale), locale]),
  );
  // languages は優先順位つきの一覧。先頭が未対応でも次の候補を拾えるよう language 単体より優先する
  const preferred = navigator.languages.length
    ? navigator.languages
    : [navigator.language];

  for (const tag of preferred) {
    const matched = byKey.get(toLocaleKey(tag));
    if (matched) {
      return matched;
    }
  }

  return FALLBACK_LOCALE;
}

export const i18n = setupI18n({
  locale: resolveLocale(),
  messages: {
    ja: jaMessages,
    en: enMessages,
    "zh-Hans": zhHansMessages,
    "zh-Hant": zhHantMessages,
    ko: koMessages,
    vi: viMessages,
    id: idMessages,
    ne: neMessages,
  },
  missing: (_locale, id) => id,
});

// values は ICU プレースホルダの補間用。source locale でも msgstr === msgid なので補間される
export function t(id: string, values?: Record<string, unknown>) {
  return i18n._(id, values);
}
